<template>
  <div class="stage">
    <div class="calculator" :class="{ flash: flashing }">
      <CalcDisplay />
      <CalcKeypad />
      <div class="brand">NovaCalc</div>
    </div>

    <transition name="panel">
      <aside v-if="store.activePanel" class="panel">
        <CalcHistory v-if="store.activePanel === 'history'" />
        <CalcGraph
          v-else-if="store.activePanel === 'graph'"
          @close="store.setPanel('graph')"
        />
        <CalcSettings v-else-if="store.activePanel === 'settings'" />
      </aside>
    </transition>
  </div>
</template>

<script setup>
import {
  defineAsyncComponent,
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import { useCalculator } from '../stores/calculator.js';
import CalcDisplay from './CalcDisplay.vue';
import CalcKeypad from './CalcKeypad.vue';
const CalcHistory = defineAsyncComponent(() => import('./CalcHistoryPro.vue'));
const CalcGraph = defineAsyncComponent(() => import('./CalcGraph.vue'));
const CalcSettings = defineAsyncComponent(() => import('./CalcSettings.vue'));

const store = useCalculator();
const flashing = ref(false);

// Flash visuel à chaque calcul validé.
watch(
  () => store.lastComputeAt,
  () => {
    flashing.value = true;
    setTimeout(() => (flashing.value = false), 320);
  }
);

// --- Clavier physique ---
function onKey(e) {
  if (
    e.defaultPrevented ||
    e.isComposing ||
    e.target?.closest?.('input, textarea, select, [contenteditable]')
  )
    return;

  const key = e.key.toLowerCase();
  if (e.ctrlKey || e.metaKey) {
    if (key === 'h')
      store.setPanel(store.activePanel === 'history' ? null : 'history');
    else if (key === 'g')
      store.setPanel(store.activePanel === 'graph' ? null : 'graph');
    else if (key === 's') store.toggleScientific();
    else return;
    e.preventDefault();
    return;
  }
  if (e.key === 'F2') {
    e.preventDefault();
    store.toggleScientific();
    return;
  }
  if (e.key === 'Delete') {
    e.preventDefault();
    store.backspace();
    return;
  }
  const k = e.key;
  if (/^[0-9.]$/.test(k)) {
    store.append(k);
  } else if (k === '+' || k === '-') {
    store.append(k);
  } else if (k === '*' || k === 'x') {
    store.append('×');
  } else if (k === '/') {
    store.append('÷');
  } else if (k === '^' || k === '%' || k === '(' || k === ')') {
    store.append(k);
  } else if (k === 'Enter' || k === '=') {
    e.preventDefault();
    store.compute();
  } else if (k === 'Backspace') {
    store.backspace();
  } else if (k === 'Escape') {
    store.clear();
  } else {
    return;
  }
}

onMounted(() => {
  document.documentElement.setAttribute('data-theme', store.theme);
  const sharedExpression = new URLSearchParams(window.location.search).get(
    'expr'
  );
  if (sharedExpression?.trim()) store.setInput(sharedExpression.slice(0, 500));
  window.addEventListener('keydown', onKey);
});

onBeforeUnmount(() => window.removeEventListener('keydown', onKey));

// Applique le thème au <html> quand il change.
watch(
  () => store.theme,
  (t) => document.documentElement.setAttribute('data-theme', t)
);
</script>

<style scoped>
.stage {
  position: relative;
  width: 100%;
  min-height: 100vh;
  padding: max(32px, 8vh) 24px 48px;
  perspective: 1400px;
}

.panel {
  position: absolute;
  z-index: 2;
  top: max(24px, 8vh);
  right: calc(50% + 210px);
  width: 340px;
  max-width: 92vw;
}

.calculator {
  width: 380px;
  max-width: 92vw;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  position: relative;
  padding: 18px 18px 24px;
  border: 1px solid color-mix(in srgb, var(--case-top) 80%, white);
  border-radius: 30px;
  background: linear-gradient(150deg, var(--case-top), var(--case-bottom) 70%);
  box-shadow:
    0 3px 0 var(--case-edge),
    0 10px 0 color-mix(in srgb, var(--case-edge) 78%, transparent),
    0 28px 35px -15px rgba(0, 0, 0, 0.55),
    0 45px 80px -20px rgba(0, 0, 0, 0.35),
    inset 1px 1px 1px rgba(255, 255, 255, 0.38),
    inset -2px -3px 6px rgba(0, 0, 0, 0.18);
  transform: rotateX(4deg);
}

.calculator::before {
  content: '';
  position: absolute;
  inset: 5px 7px auto;
  height: 50%;
  border-radius: 24px 24px 60% 60%;
  background: linear-gradient(rgba(255, 255, 255, 0.07), transparent);
  pointer-events: none;
}

.calculator > * {
  position: relative;
}

.calculator.flash :deep(.display) {
  animation: flash 320ms ease;
}

@keyframes flash {
  0% {
    box-shadow: var(--shadow);
  }
  40% {
    box-shadow:
      0 0 0 3px var(--glow),
      var(--shadow);
  }
  100% {
    box-shadow: var(--shadow);
  }
}

.brand {
  text-align: center;
  margin-top: 20px;
  font-size: 0.8rem;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: var(--text-dim);
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.16);
  opacity: 0.85;
}

.panel-enter-active,
.panel-leave-active {
  transition:
    opacity 260ms ease,
    transform 260ms ease;
}
.panel-enter-from,
.panel-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

@media (max-width: 1100px) {
  .stage {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .panel {
    position: relative;
    top: auto;
    right: auto;
    order: 2;
    margin-top: 20px;
  }

  .calculator {
    order: 1;
    width: min(380px, 100%);
    min-width: 0;
    margin: 0;
  }
}

@media (max-width: 420px) {
  .stage {
    padding: 24px 12px 36px;
  }

  .calculator {
    max-width: 100%;
    padding: 14px 14px 20px;
    transform: none;
  }
}
</style>
